import React, { useMemo, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AssetHierarchyBrowser from '../AssetHierarchyBrowser';
import io from '../../io';
import { Typography } from '@totalsoft/rocket-ui';
import { useMutation, useSubscription } from 'hooks/apollo';
import { autoScroll } from '../../functions';
import AssetHierarchyDetailsContainer from '../details/AssetHierarchyDetailsContainer';
import { useToast } from 'hooks/toasts/useToast';

const initialState = { hierarchy: { id: null, name: null }, triggers: { viewDetails: false } };
const HierarchyMappingContainer = ({ hierarchy }) => {
  const [state, setState] = useState(initialState);
  const { t } = useTranslation();
  const addToast = useToast();
  const refs = { detailsArea: useRef(null) };

  const handleClose = useCallback(trigger => () => setState(prev => ({ ...prev, triggers: { ...prev.triggers, [trigger]: false } })), []);

  const [mapAssetHierarchies] = useMutation(io.mutations.MAP_ASSET_HIERARCHIES);

  useSubscription(io.subscriptions.ASSET_HIERARCHIES_MAPPED, {
    onData: () => {
      addToast(t('AssetHierarchy.List.Actions.MapWithAnother.Confirmation'), 'success');
    }
  });

  const viewHierarchyDetails = useCallback(
    hierarchy => {
      setState(prev => ({ hierarchy, triggers: { ...prev.triggers, viewDetails: true } }));
      autoScroll(refs.detailsArea);
    },
    [refs.detailsArea]
  );

  const hierarchyActions = useMemo(
    () => [
      {
        label: 'AssetHierarchy.List.Actions.ViewDetails.Label',
        effect: viewHierarchyDetails
      },
      {
        label: 'AssetHierarchy.List.Actions.MapWithAnother.Map',
        effect: dest => {
          mapAssetHierarchies({ variables: { input: { mainHierarchyId: hierarchy.id, minorHierarchyId: dest.id } } });
        }
      }
    ],
    [mapAssetHierarchies, hierarchy.id, viewHierarchyDetails]
  );

  const events = useMemo(
    () => ({
      onDisableHierarchy: minorHierarchy => hierarchy.id === minorHierarchy.id
    }),
    [hierarchy.id]
  );

  return (
    <>
      <AssetHierarchyBrowser
        title={t('AssetHierarchy.List.Actions.MapWithAnother.Title', hierarchy)}
        hierarchyActions={hierarchyActions}
        events={events}
        position='minor'
      />
      <div ref={refs.detailsArea} />
      {state.triggers.viewDetails && <AssetHierarchyDetailsContainer hierarchy={state.hierarchy} onClose={handleClose('viewDetails')} />}
      <Typography variant='caption' display='block' gutterBottom>
        {`* ${t('AssetHierarchy.List.Actions.MapWithAnother.Content')}`}
      </Typography>
    </>
  );
};

HierarchyMappingContainer.propTypes = {
  hierarchy: PropTypes.object.isRequired
};

export default HierarchyMappingContainer;
